import React, { useState, useEffect } from 'react';
import { partners as mockPartners } from '../mockData';
import { MapPin, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners(mockPartners); // Fallback to mock data
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Partners
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Trusted by leading food brands across the UK
            </p>
          </div>
        </div>
      </section>

      {/* Featured Partners */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Building authentic food brands together
            </p>
          </div>

          <div className="space-y-16">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading partners...</p>
              </div>
            ) : (
              partners.map((partner, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <img
                    src={partner.image}
                    alt={partner.name}
                    className="w-full h-96 object-cover rounded-none shadow-xl hover:shadow-2xl transition-all duration-300"
                  />
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-none inline-block mb-4">
                    PARTNER
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 mb-3">
                    {partner.name}
                  </h3>
                  <div className="flex items-center gap-2 text-emerald-600 mb-6">
                    <MapPin size={20} />
                    <span className="font-medium text-lg">{partner.location}</span>
                  </div>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    {partner.description}
                  </p>
                  <div className="bg-gray-50 border-l-4 border-emerald-600 p-6 rounded-none">
                    <div className="flex gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} size={20} className="text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                    <p className="text-gray-800 italic text-lg leading-relaxed">
                      "{partner.testimonial}"
                    </p>
                  </div>
                </div>
              </div>
            ))
            )}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Brands Choose Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              What our partners say about working with Fresh Bites Group
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-none shadow-lg">
              <div className="text-5xl font-bold text-emerald-600 mb-4">95%</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Partner Satisfaction</h3>
              <p className="text-gray-600">
                Our partners consistently rate their experience with us as excellent
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg">
              <div className="text-5xl font-bold text-blue-600 mb-4">50+</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Products Launched</h3>
              <p className="text-gray-600">
                Successfully brought dozens of authentic recipes to market
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg">
              <div className="text-5xl font-bold text-emerald-600 mb-4">100%</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Compliance</h3>
              <p className="text-gray-600">
                Perfect track record of meeting UK food safety standards
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Our Partners Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-none shadow-lg border-t-4 border-emerald-600">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={18} className="text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-6">
                "The team's expertise in traditional recipes is exceptional. They helped us maintain authenticity while scaling production."
              </p>
              <div className="border-t pt-4">
                <p className="font-bold text-gray-900">Rajesh Kumar</p>
                <p className="text-gray-600 text-sm">The Product of Andhra</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg border-t-4 border-blue-600">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={18} className="text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-6">
                "Professional, reliable, and committed to quality. Fresh Bites has been instrumental in our growth."
              </p>
              <div className="border-t pt-4">
                <p className="font-bold text-gray-900">Priya Sharma</p>
                <p className="text-gray-600 text-sm">Vedique Resto Bar</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg border-t-4 border-emerald-600">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={18} className="text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-6">
                "Their manufacturing quality and consistency make them the perfect distribution partner for our network."
              </p>
              <div className="border-t pt-4">
                <p className="font-bold text-gray-900">David Thompson</p>
                <p className="text-gray-600 text-sm">Daily Crop</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Become Our Next Success Story
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
            Join our growing network of successful food brands across the UK
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-emerald-600 px-10 py-5 rounded-none font-bold text-lg transition-all duration-300 hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1"
          >
            Partner With Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Partners;
